/**
 * Converts structured audit data into flat row arrays for CSV/Excel/PDF export.
 * Each function returns an array of plain objects suitable for export services.
 */

import { AuditResult } from "../services/auditService";

export function flattenKeywords(keywords: any[]): Record<string, any>[] {
  return keywords.map((kw) => ({
    Keyword: kw.keyword ?? "",
    Position: kw.position ?? kw.pos ?? "—",
    Volume: kw.searchVolume ?? kw.volume ?? 0,
    Difficulty: kw.difficulty ?? "—",
    CPC: kw.cpc ?? "—",
    "Traffic Potential": kw.trafficPotential ?? kw.traffic ?? "—",
    ...(kw.source ? { source: kw.source } : {}),
  }));
}

export function flattenPositionDistribution(positions: any[]): Record<string, any>[] {
  return positions.map((p) => ({
    Period: `${p.month}/${p.year}`,
    Keywords: p.keywordsCount ?? 0,
    "Top 1-5": p.top1_5 ?? 0,
    "Top 6-10": p.top6_10 ?? 0,
    "Top 11-20": p.top11_20 ?? 0,
    "Top 21-50": p.top21_50 ?? 0,
    "Top 51-100": p.top51_100 ?? 0
  }));
}

export function flattenPositionChanges(keywords: any[]): Record<string, any>[] {
  return keywords.map((kw) => ({
    Keyword: kw.keyword ?? "",
    Position: kw.position ?? kw.pos ?? "—",
    "Previous Position": kw.prevPosition ?? "—",
    Change: kw.change ?? "—"
  }));
}

export function flattenCompetitors(competitors: any[]): Record<string, any>[] {
  return competitors.map((c) => ({
    Domain: c.domain ?? "",
    "Authority Score": c.authorityScore ?? c.authority ?? "—",
    Traffic: c.traffic ?? 0,
    "Common Keywords": c.commonKeywords ?? "—",
    Overlap: `${c.overlap}%`
  }));
}

export function flattenTechnicalIssues(issues: any[]): Record<string, any>[] {
  return issues.map((i) => ({
    Issue: i.message ?? i.issue ?? i.title ?? "",
    Severity: i.severity ?? "—",
    Detail: i.detail ?? i.description ?? "",
  }));
}

export function flattenBacklinks(backlinks: any): Record<string, any>[] {
  const rows: Record<string, any>[] = [];
  if (backlinks?.topSources) {
    for (const s of backlinks.topSources) {
      rows.push({ Type: "Top Source", Domain: s.domain, Backlinks: s.backlinks, DA: s.domainAuthority ?? "—", PA: s.pageAuthority ?? "—" });
    }
  }
  if (backlinks?.anchors) {
    for (const a of backlinks.anchors) {
      rows.push({ Type: "Anchor", "Anchor Text": a.anchor ?? "(empty)", Backlinks: a.backlinksCount ?? 0, "Ref. Domains": a.referringDomainsCount ?? 0 });
    }
  }
  if (backlinks?.newLinks) {
    for (const l of backlinks.newLinks) {
      rows.push({ Type: "New Link", "From URL": l.urlFrom, "To URL": l.urlTo, Anchor: l.anchor ?? "—", "First Seen": l.firstSeen ?? "—" });
    }
  }
  if (backlinks?.lostLinks) {
    for (const l of backlinks.lostLinks) {
      rows.push({ Type: "Lost Link", "From URL": l.urlFrom, "To URL": l.urlTo, Anchor: l.anchor ?? "—", "Last Seen": l.lastSeen ?? "—" });
    }
  }
  return rows;
}

export function flattenTrafficHistory(history: any[]): Record<string, any>[] {
  return history.map((h) => ({
    Period: `${h.month}/${h.year}`,
    Traffic: h.trafficSum ?? 0,
    Keywords: h.keywordsCount ?? 0,
    "Top 1-5": h.top1_5 ?? 0,
    "Top 6-10": h.top6_10 ?? 0,
    "Top 11-20": h.top11_20 ?? 0,
  }));
}

export function flattenSerpFeatures(serpFeatures: any[]): Record<string, any>[] {
  return serpFeatures.map((sf) => ({
    Keyword: sf.keyword ?? "",
    Position: sf.position ?? "—",
    Volume: sf.volume ?? 0,
    Traffic: sf.traffic ?? 0,
    "SERP Features": (sf.serpFeatures ?? []).join(", "),
  }));
}

export function flattenTopPages(topPages: any[]): Record<string, any>[] {
  return topPages.map((p) => ({
    URL: p.url ?? "",
    Keywords: p.keywordsCount ?? 0,
    Traffic: p.trafficSum ?? 0,
  }));
}

export function flattenAIEngines(engines: Record<string, any>): Record<string, any>[] {
  return Object.entries(engines).map(([name, eng]) => ({
    Engine: name,
    "Brand Presence": eng.error ? "Error" : `${eng.brandPresence ?? 0}%`,
    "Link Presence": eng.error ? "Error" : `${eng.linkPresence ?? 0}%`,
    "Avg. Position": eng.error ? "Error" : (eng.averagePosition ?? 0).toFixed(1),
    Error: eng.error ?? "",
  }));
}

export function flattenLeaderboard(leaderboard: any[]): Record<string, any>[] {
  return leaderboard.map((entry, i) => ({
    Rank: i + 1,
    Domain: entry.domain ?? "",
    "Brand Presence": `${entry.brandPresence ?? 0}%`,
    "Link Presence": `${entry.linkPresence ?? 0}%`,
    "Avg. Position": (entry.averagePosition ?? 0).toFixed(1),
  }));
}

export function flattenMetrics(metrics: Record<string, any>): Record<string, any>[] {
  return Object.entries(metrics).map(([key, value]) => ({
    Metric: key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()),
    Value: typeof value === "number" ? value.toLocaleString() : String(value ?? "—"),
  }));
}

// Get Metrics data
export function getMetricsData(r: any, auditType: string) {
  let title = "Summary Metrics";
  let metrics: { label: string; value: string }[] = [];
  let summaryMetrics: { label: string; value: string }[] = [];

  const kw = r?.keywords || [];
  const sf = r?.serpFeatures || [];
  const cm = r?.currentMetrics || {};
  const comp = r?.competitors || [];
  const dm = r?.domainMetrics || {};
  const ts = r?.technicalSEO || {};
  const bl = r?.backlinks || {};
  const da = bl?.domainAuthority || {};

  switch (auditType) {
    case "traffic-trends": {
      title = "Current Traffic Metrics";

      metrics = [
        { label: "Overall Score", value: String(cm?.authorityScore ?? 0) },
        { label: "Organic Traffic", value: (cm.organicTraffic ?? 0).toLocaleString() },
        { label: "Keywords", value: (cm.organicKeywords ?? 0).toLocaleString() },
        { label: "Organic Cost", value: `$${(cm.organicCost ?? 0).toLocaleString()}` },
        { label: "Top 1-5", value: (cm.top1_5 ?? 0).toLocaleString() }
      ];
      summaryMetrics = [
        ...metrics.slice(1),
        { label: "Top 6-10", value: (cm.top6_10 ?? 0).toLocaleString() },
        { label: "Top 11-20", value: (cm.top11_20 ?? 0).toLocaleString() }
      ]
      break;
    }

    case "authority-trust":
    case "backlinks": {

      title = auditType === "backlinks" ? "Backlink Profile Overview" : "Domain Authority & Trust";

        metrics = [
          { label: "Overall Score", value: String(da?.domainAuthority ?? 0) },
          { label: "Page Authority", value: String(da.pageAuthority ?? 0) },
          { label: "Total Backlinks", value: (bl.total ?? 0).toLocaleString() },
          { label: "Referring Domains", value: (bl.referringDomains ?? 0).toLocaleString() },
          { label: "Anchor Diversity", value: (bl.anchorDiversity ?? 0).toLocaleString() }
        ];
      summaryMetrics = [
        { label: "Domain Authority", value: String(da?.domainAuthority ?? 0) },
        ...metrics.slice(1),
        { label: "Top Sources", value: (bl?.topSources.length ?? 0).toLocaleString() }
      ]
      break;
    }

    case "keyword-rankings": {
      metrics = [
        { label: "Keywords", value: String(kw.length ?? 0) },
        { label: "Avg. Difficulty", value: kw.length ? (kw.reduce((s: number, k: any) => s + (k.difficulty ?? 0), 0) / kw.length).toFixed(0) : "0" },
        { label: "Avg. Position", value: kw?.length ? (kw.reduce((sum: number, k: any) => sum + k.position, 0) / kw.length).toFixed(1) : "0" },
        { label: "Traffic Potential", value: kw.reduce((sum: number, k: any) => sum + (k.trafficPotential || 0), 0).toLocaleString() },
        { label: "Total Volume", value: kw.reduce((sum: number, k: any) => sum + (k.searchVolume || 0), 0).toLocaleString() }
      ];
      break;
    }

    case "serp-analysis": {
      metrics = [
        { label: "SERP Keywords", value: String(sf?.length ?? 0) },
        { label: "Keyword Metrics", value: String(r.keywordMetrics?.length ?? 0) },
        { label: "Traffic Potential", value: sf.reduce((sum: number, k: any) => sum + (k.traffic || 0), 0).toLocaleString() },
        { label: "Avg. Position", value: sf?.length ? (sf.reduce((sum: number, k: any) => sum + k.position, 0) / sf.length).toFixed(1) : "0" },
        { label: "Total Volume", value: sf.reduce((sum: number, k: any) => sum + (k.volume || 0), 0).toLocaleString() }
      ];
      break;
    }

    case "competitor-analysis": {
      metrics = [
        { label: "Competitors", value: String(comp?.length ?? 0) },
        { label: "Your Authority", value: String(dm?.authorityScore ?? 0) },
        { label: "Your Traffic", value: (dm?.organicTraffic).toLocaleString() },
        { label: "Your Keywords", value: (dm?.organicKeywords).toLocaleString() },
        { label: "Backlinks", value: (dm?.backlinks ?? 0).toLocaleString() }
      ];
      break;
    }

    case "technical-seo":
    case "page-level-audit": {
      title = auditType === "technical-seo" ? "Crawl Statistics" : "Page-Level Analysis";

      metrics = [
        { label: "Technical Score", value: `${ts.score ?? 0}` },
        { label: "Content Length", value: (ts.contentLength ?? 0).toLocaleString() },
        { label: "Internal Links", value: String(ts.internalLinks ?? 0) },
        { label: "Critical Issues", value: String(ts?.issues?.filter((i: any) => i.severity === "critical").length ?? 0) },
        { label: "Warnings", value: String(ts?.issues?.filter((i: any) => i.severity === "warning").length ?? 0) }
      ];
      summaryMetrics = [
        { label: "Score", value: `${ts.score ?? 0}/100` },
        { label: "Content Length", value: (ts.contentLength ?? 0).toLocaleString() },
        { label: "Internal Links", value: String(ts.internalLinks ?? 0) },
        { label: "External Links", value: String(ts.externalLinks ?? 0) },
        { label: "Images", value: String(ts.images?.total ?? 0) },
        { label: "Issues", value: String(ts.issues?.length ?? 0) },
        { label: "Schema Markup", value: String(ts.hasSchema ? "Yes" : "No") }
      ];
      break;
    }

    case "content-gaps": {

      title = "Content Quality Metrics";
      metrics = [
        { label: "Quality", value: String(ts.score ?? 0) },
        { label: "Content Gaps", value: String(comp?.flatMap((c: any) => c.keywordGaps || []).length || "0") },
        { label: "Avg. Overlap %", value: comp?.length ? (comp.reduce((sum: number, c: any) => sum + (c.overlap || 0), 0) / comp.length).toFixed(1) + "%" : "0%" },
        { label: "Competitor Traffic", value: (comp || []).reduce((sum: number, c: any) => sum + (c.traffic || 0), 0).toLocaleString() },
        { label: "Organic Keywords", value: (dm.organicKeywords ?? 0).toLocaleString() },
      ];
      summaryMetrics = [
        { label: "Score", value: `${ts.score ?? 0}/100` },
        { label: "Content Length", value: (ts.contentLength ?? 0).toLocaleString() },
        { label: "Internal Links", value: String(ts.internalLinks ?? 0) },
        { label: "External Links", value: String(ts.externalLinks ?? 0) },
        { label: "Images", value: String(ts.images?.total ?? 0) },
        { label: "Issues", value: String(ts.issues?.length ?? 0) },
        { label: "Schema Markup", value: String(ts.hasSchema ? "Yes" : "No") }
      ];
      break;
    }

    case "keyword-discovery": {
      metrics = [
        { label: "Keywords", value: String(kw.length ?? 0) },
        { label: "High Volume", value: String(kw.filter((k: any) => k.volume > 100000).length) },
        { label: "Avg. Difficulty", value: kw.length ? (kw.reduce((s: number, k: any) => s + (k.difficulty ?? 0), 0) / kw.length).toFixed(0) : "0" },
        { label: "Avg. cpc", value: (kw).reduce((s: number, k: any) => s + (k.cpc || 0), 0).toLocaleString() },
        { label: "Total Volume", value: (kw).reduce((sum: number, k: any) => sum + (k.volume || 0), 0).toLocaleString() }
      ];
      break;
    }

    case "domain-pages": {
      metrics = [
        { label: "Quality", value: String(ts?.score ?? 0) },
        { label: "Total pages", value: String(r.topPages?.length ?? 0) },
        { label: "Thin Pages", value: String(r?.topPages?.filter((p: any) => (p.keywordsCount || 0) < 5 || (p.trafficSum || 0) < 100).length || '0') },
        { label: "Refresh Needed", value: String(r?.topPages?.filter((p: any) => (p.keywordsCount || 0) > 0 && (p.trafficSum || 0) < 50).length || '0') },
        { label: "Issues", value: String(ts.issues.length ?? 0) }
      ];
      break;
    }

    case "full-seo-audit": {
      title = "Domain Overview";
      metrics = [
        { label: "Overall Score", value: String(dm.authorityScore ?? 0) },
        { label: "Organic Traffic", value: (dm.organicTraffic ?? 0).toLocaleString() },
        { label: "Organic Keywords", value: (dm.organicKeywords ?? 0).toLocaleString() },
        { label: "Backlinks", value: (dm.backlinks ?? 0).toLocaleString() },
        { label: "Technical Score", value: `${ts?.score || 0}/100` }
      ];
      summaryMetrics = [
        { label: "Authority Score", value: `${dm.authorityScore ?? 0}/100` },
        ...metrics.slice(1),
        { label: "Referring Domains", value: (dm?.referringDomains || 0).toLocaleString() },
      ];
      break;
    }
  }

  return { title, metrics, summaryMetrics };
}

// Get Keywords 
export function getKeywordsData(r: any, auditType: string) {
  let keywordTitle = "Keywords";

  let keywordData: AuditResult["keywords"];
  switch (auditType) {
    case "serp-analysis":
      keywordTitle = "Keyword Metrics";
      keywordData = r.keywordMetrics || [];
      break;
    case "competitor-analysis":
      keywordTitle = "Keyword Gap Analysis";
      keywordData = r?.competitors?.flatMap((c: any) => c.keywordGaps) || [];
      break;
    case "content-gaps":
      keywordTitle = "Content Gap Analysis";
      keywordData = r?.competitors?.flatMap((c: any) => c.keywordGaps) || [];
      break;
    case "domain-pages":
      keywordTitle = "Page Keywords";
      keywordData = r?.topPages?.flatMap((p: any) => p?.keywords || []) || [];
      break;
    case "keyword-rankings":
      keywordTitle = "Keyword Rankings";
      keywordData = r.keywords || [];
      break;
    case "keyword-discovery":
      keywordTitle = "Discovered Keywords";
      keywordData = r.keywords || [];
      break;
    case "full-seo-audit":
      keywordTitle = "Keyword Performance";
      keywordData = r.keywords || [];
      break;
    default:
      keywordTitle = "Keywords";
      keywordData = r.keywords || [];
  }

  return {
    keywordTitle,
    keywordData: Array.isArray(keywordData) ? keywordData : [],
  };

}

/**
 * Build a complete export dataset from any audit result.
 * Returns flat rows suitable for CSV/Excel.
 */
export function buildFullExportData(
  auditResult: any,
  auditType: string
): Record<string, any>[] {
  const rows: Record<string, any>[] = [];

  const includeTechnical = ["full-seo-audit", "technical-seo"].includes(auditType);

  const includeBacklinks = ["backlinks", "authority-trust",].includes(auditType);

  const includeTopPages = ["page-level-audit", "domain-pages",].includes(auditType);

  const includeSERP = ["serp-analysis", "keyword-discovery",].includes(auditType);

  const includeHistory = ["traffic-trends", "full-seo-audit",].includes(auditType);

  // ── SUMMARY METRICS ──
  const { title, summaryMetrics } = getMetricsData(auditResult, auditType);

  if (summaryMetrics.length) {
    summaryMetrics.forEach((m) => {
      rows.push({ Section: title, Metric: m.label, Value: m.value });
    });
  }


  // ── KEYWORDS (DYNAMIC BASED ON AUDIT TYPE) ──
  const { keywordTitle, keywordData } = getKeywordsData(auditResult, auditType);

  if (keywordData.length) {
    flattenKeywords(keywordData).forEach((r) =>
      rows.push({ Section: keywordTitle, ...r })
    );
  }


  // ── POSTION DISTRIBUTION & POSITION CHANGES──
  if (auditType === "keyword-rankings") {
    flattenPositionDistribution(auditResult.positionDistribution).forEach((r) =>
      rows.push({ Section: "Postion Distribution", ...r })
    );
    flattenPositionChanges(auditResult.positionChanges).forEach((r) =>
      rows.push({ Section: "Postion Changes", ...r })
    );
  }

  // ── CONTENT REFRESH OPPORTUNITIES ──
  if (auditType === "content-gaps" && auditResult.contentRefreshOpportunities?.length) {
    auditResult.contentRefreshOpportunities.forEach((r) =>
      rows.push({ Section: "Content Refresh Opportunities", Keyword: r.keyword, URL: r.url, Volume: r.searchVolume, Opportunity: r.opportunity, Reason: r.reason })
    );
  }

  // ── COMPETITORS ──
  if (auditType === "competitor-analysis" && auditResult.competitors?.length) {
    flattenCompetitors(auditResult.competitors).forEach((r) =>
      rows.push({ Section: "Competitors", ...r })
    );
    auditResult?.competitors.forEach((r) =>
      rows.push({ Section: "Estimated Market Share", Domain: r.domain, Traffic: r.traffic, "Market Share%": `${r.share}%` })
    );
  }

  // ── TECHNICAL SEO ──
  if (includeTechnical && auditResult.technicalSEO?.issues?.length) {
    flattenTechnicalIssues(auditResult.technicalSEO.issues).forEach((r) =>
      rows.push({ Section: "Technical Issues", ...r })
    );
  }

  // ── BACKLINKS ──
  if (includeBacklinks && auditResult.backlinks) {
    flattenBacklinks(auditResult.backlinks).forEach((r) =>
      rows.push({ Section: "Backlinks", ...r })
    );
  }

  // ── TRAFFIC HISTORY ──
  if (includeHistory && auditResult.history?.length) {
    flattenTrafficHistory(auditResult.history).forEach((r) =>
      rows.push({ Section: "Traffic History", ...r })
    );
  }

  // ── WORLDWIDE TRAFFIC ──
  if (auditResult.worldwideOverview?.length) {
    auditResult.worldwideOverview.forEach((w) =>
      rows.push({ Section: "Worldwide Traffic", Region: w.source, Traffic: w.traffic, Keywords: w.keywords, "Est. Cost": w.price })
    );
  }

  // ── SERP FEATURES ──
  if (includeSERP && auditResult.serpFeatures?.length) {
    flattenSerpFeatures(auditResult.serpFeatures).forEach((r) =>
      rows.push({ Section: "SERP Features", ...r })
    );
  }

  // ── TOP PAGES ──
  if (includeTopPages && auditResult.topPages?.length) {
    flattenTopPages(auditResult.topPages).forEach((r) =>
      rows.push({ Section: "Top Pages", ...r })
    );
  }

  // ── AI VISIBILITY ──
  if (auditType === "full-seo-audit" && auditResult.aiVisibility?.engines) {
    flattenAIEngines(auditResult.aiVisibility.engines).forEach((r) =>
      rows.push({ Section: "AI Engines", ...r })
    );
  }

  if (auditType === "full-seo-audit" && auditResult.aiVisibility?.leaderboard?.length) {
    flattenLeaderboard(auditResult.aiVisibility.leaderboard).forEach((r) =>
      rows.push({ Section: "AI Leaderboard", ...r })
    );
  }

  return rows.length > 0
    ? rows
    : [{ Section: "Analysis", Content: auditResult.analysis ?? "No data" }];
}